import { useSEO } from '../../hooks/useSEO';
import WebdesignHero from './components/WebdesignHero';
import WebdesignServices from './components/WebdesignServices';
import WebdesignPortfolio from './components/WebdesignPortfolio';
import WebdesignProcess from './components/WebdesignProcess';
import WebdesignPricing from './components/WebdesignPricing';
import WebdesignCTA from './components/WebdesignCTA';

const WebdesignPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bamme-media.de';

  useSEO({
    title: 'Webdesign Wernigerode | Websites & Shops | Bamme-Media',
    description: 'Professionelles Webdesign in Wernigerode von Bamme-Media. Moderne, responsive Websites, Online-Shops und WordPress-Entwicklung. SEO-optimiert und mobilfreundlich. Jetzt kostenlose Beratung!',
    keywords: 'Webdesign Wernigerode, Website erstellen, Online Shop, WordPress, Responsive Design, SEO, Webentwicklung, Homepage, E-Commerce, Bamme-Media',
    canonical: `${siteUrl}/webdesign-entwicklung`,
    robots: 'index, follow',
    ogTitle: 'Webdesign Wernigerode | Websites & Shops | Bamme-Media',
    ogDescription: 'Professionelles Webdesign in Wernigerode von Bamme-Media. Moderne, responsive Websites, Online-Shops und WordPress-Entwicklung. SEO-optimiert und mobilfreundlich.',
    ogImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    ogImageWidth: '1200',
    ogImageHeight: '630',
    ogImageAlt: 'Bamme-Media Webdesign Services in Wernigerode',
    twitterTitle: 'Webdesign Wernigerode | Websites & Shops | Bamme-Media',
    twitterDescription: 'Professionelles Webdesign in Wernigerode von Bamme-Media. Moderne, responsive Websites, Online-Shops und WordPress-Entwicklung. SEO-optimiert und mobilfreundlich.',
    twitterImage: 'https://static.readdy.ai/image/8fd098698619b27c03cb78789aa098a8/fa291c43a7c1ed26d5ebb74eb50592e0.png',
    twitterImageAlt: 'Bamme-Media Webdesign Services in Wernigerode',
    geoPosition: '51.8312,10.7865',
    geoRegion: 'DE-ST',
    geoPlacename: 'Wernigerode',
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Webdesign & Webentwicklung",
        "description": "Professionelles Webdesign in Wernigerode von Bamme-Media. Moderne, responsive Websites, Online-Shops und WordPress-Entwicklung. SEO-optimiert und mobilfreundlich.",
        "provider": {
          "@type": "Organization",
          "name": "Bamme-Media",
          "url": siteUrl,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Wernigerode",
            "postalCode": "38855",
            "addressRegion": "Sachsen-Anhalt",
            "addressCountry": "DE"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "51.8312",
            "longitude": "10.7865"
          }
        },
        "areaServed": [
          {
            "@type": "Place",
            "name": "Wernigerode"
          },
          {
            "@type": "Place",
            "name": "Harz"
          },
          {
            "@type": "Place",
            "name": "Sachsen-Anhalt"
          }
        ],
        "serviceType": "Web Design and Development",
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Webdesign Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Responsive Webdesign",
                "description": "Mobile-optimierte Websites für alle Geräte und Bildschirmgrößen"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Online Shop Entwicklung",
                "description": "E-Commerce Lösungen und Online-Shops für den digitalen Verkauf"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "WordPress Entwicklung",
                "description": "Maßgeschneiderte WordPress Websites mit CMS-Funktionalität"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "SEO-Optimierung",
                "description": "Suchmaschinenoptimierung für bessere Google-Rankings"
              }
            }
          ]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Was kostet eine professionelle Website?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Die Kosten variieren je nach Umfang und Anforderungen. Eine einfache Website startet bei ca. 1.500€, während umfangreichere Projekte mit Shop-Systemen oder individuellen Funktionen zwischen 3.000€ und 10.000€ liegen können. Wir erstellen dir gerne ein individuelles Angebot basierend auf deinen spezifischen Bedürfnissen."
            }
          },
          {
            "@type": "Question",
            "name": "Wie lange dauert die Entwicklung einer Website?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Die Entwicklungszeit hängt vom Projektumfang ab. Eine Standard-Website ist in der Regel innerhalb von 4-6 Wochen fertig. Komplexere Projekte mit individuellen Funktionen oder E-Commerce-Lösungen können 8-12 Wochen in Anspruch nehmen. Wir halten dich während des gesamten Prozesses auf dem Laufenden."
            }
          },
          {
            "@type": "Question",
            "name": "Bietet ihr auch Hosting und Domain-Verwaltung an?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, wir bieten vollständige Hosting-Lösungen an. Das umfasst die Domain-Registrierung, SSL-Zertifikate, regelmäßige Backups und technischen Support. Du kannst dich voll auf dein Business konzentrieren, während wir uns um die technische Infrastruktur kümmern."
            }
          },
          {
            "@type": "Question",
            "name": "Was ist der Unterschied zwischen WordPress und Custom-Entwicklung?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "WordPress ist ein flexibles Content-Management-System, ideal für Blogs und klassische Unternehmenswebsites mit regelmäßigen Inhalts-Updates. Custom-Entwicklung bietet maximale Flexibilität und Performance für spezielle Anforderungen, komplexe Funktionen oder einzigartige Designs. Wir beraten dich, welche Lösung am besten zu deinen Zielen passt."
            }
          },
          {
            "@type": "Question",
            "name": "Ist die Website auch auf Smartphones und Tablets optimiert?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Absolut! Alle unsere Websites sind vollständig responsive und für alle Geräte optimiert. Da über 60% der Nutzer mobil surfen, legen wir besonderen Wert auf perfekte Darstellung und Bedienbarkeit auf Smartphones und Tablets. Das ist bei uns Standard, nicht optional."
            }
          },
          {
            "@type": "Question",
            "name": "Bietet ihr auch SEO-Optimierung an?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, SEO ist ein integraler Bestandteil unserer Webentwicklung. Wir implementieren technisches SEO (schnelle Ladezeiten, sauberer Code, mobile Optimierung), On-Page-SEO (Meta-Tags, strukturierte Daten, optimierte Inhalte) und beraten dich zu Content-Strategien. Auf Wunsch bieten wir auch fortlaufende SEO-Betreuung an."
            }
          },
          {
            "@type": "Question",
            "name": "Kann ich die Website später selbst bearbeiten?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ja, bei WordPress-Projekten erhältst du ein benutzerfreundliches Backend, mit dem du Texte, Bilder und Inhalte selbst aktualisieren kannst. Wir schulen dich gerne in der Bedienung. Für technische Anpassungen oder Design-Änderungen stehen wir dir natürlich jederzeit zur Verfügung."
            }
          },
          {
            "@type": "Question",
            "name": "Was passiert nach dem Launch der Website?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nach dem Launch bieten wir verschiedene Wartungspakete an, die regelmäßige Updates, Sicherheits-Checks, Backups und technischen Support umfassen. Du kannst zwischen monatlichen oder jährlichen Paketen wählen. Natürlich stehen wir auch für Erweiterungen und neue Features zur Verfügung."
            }
          }
        ]
      }
    ]
  });

  return (
    <div>
      <WebdesignHero />
      <WebdesignServices />
      <WebdesignPortfolio />
      <WebdesignProcess />
      <WebdesignPricing />
      <WebdesignCTA />
    </div>
  );
};

export default WebdesignPage;
